import { Component, OnDestroy } from "@angular/core";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { PublishStateService } from "app/js/src/infrastructure/clipping-studio/publish-state.service";
import {
  DestinationMetadataOverride,
  DestinationType,
  PublishMetadata,
  SocialAccount,
  TITLE_MAX_LENGTH,
} from "app/js/src/domain/clipping-studio/publish/publish-models";

@Component({
  selector: "evrideo-clipping-studio-metadata-tab",
  templateUrl: "./clipping-studio-metadata-tab.component.html",
  styleUrls: ["./clipping-studio-metadata-tab.component.scss"],
})
export class ClippingStudioMetadataTabComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  metadata: PublishMetadata | null = null;
  isAICompleting = false;
  canRunAiMetadata = false;
  initialDefaults = {
    title: "",
    tags: "",
    message: "",
  };

  private allowedRatingsForAi: string[] = [];

  /** Selected destination id for metadata overrides (All destinations when null). */
  selectedDestinationId: string | null = null;

  /** Per-destination metadata overrides from publish state. */
  destinationMetadataOverrides: Record<string, DestinationMetadataOverride> = {};

  readonly titleMaxLength = TITLE_MAX_LENGTH;

  constructor(public publishState: PublishStateService) {
    this.publishState.metadata$.pipe(takeUntil(this.destroy$)).subscribe((m) => {
      this.metadata = m;
    });
    this.publishState.isAICompleting$.pipe(takeUntil(this.destroy$)).subscribe((v) => {
      this.isAICompleting = v;
    });
    this.publishState.canRunAiMetadata$.pipe(takeUntil(this.destroy$)).subscribe((v) => {
      this.canRunAiMetadata = v;
    });
    this.publishState.destinationMetadataOverrides$
      .pipe(takeUntil(this.destroy$))
      .subscribe((o) => {
        this.destinationMetadataOverrides = o ?? {};
      });
  }

  /** Override for the currently selected destination, if any. */
  get currentOverride(): DestinationMetadataOverride | null {
    if (!this.selectedDestinationId) return null;
    return this.destinationMetadataOverrides[this.selectedDestinationId] ?? null;
  }

  get effectiveTitle(): string {
    if (this.currentOverride?.title !== undefined) return this.currentOverride.title;
    return this.metadata?.title ?? "";
  }

  get titleCharacterCount(): number {
    return this.effectiveTitle.length;
  }

  get titleLimitExceeded(): boolean {
    return this.titleCharacterCount > this.titleMaxLength;
  }

  get effectiveMessage(): string {
    if (this.currentOverride?.message !== undefined) return this.currentOverride.message;
    return this.metadata?.message ?? "";
  }

  get effectiveTags(): string {
    if (this.currentOverride?.tags !== undefined) return this.currentOverride.tags;
    return this.metadata?.tags ?? "";
  }

  get hasTitleOverride(): boolean {
    return this.currentOverride?.title !== undefined;
  }

  get hasMessageOverride(): boolean {
    return this.currentOverride?.message !== undefined;
  }

  get hasTagsOverride(): boolean {
    return this.currentOverride?.tags !== undefined;
  }

  get hasAnyDestinationOverride(): boolean {
    const o = this.currentOverride;
    if (!o) return false;
    return (
      o.title !== undefined ||
      o.message !== undefined ||
      o.tags !== undefined
    );
  }

  setEffectiveTitle(value: string): void {
    if (this.selectedDestinationId) {
      this.publishState.setDestinationMetadataOverride(
        this.selectedDestinationId,
        "title",
        value,
      );
    } else {
      this.publishState.updateMetadataField("title", value);
    }
  }

  setEffectiveMessage(value: string): void {
    if (this.selectedDestinationId) {
      this.publishState.setDestinationMetadataOverride(
        this.selectedDestinationId,
        "message",
        value,
      );
    } else {
      this.publishState.updateMetadataField("message", value);
    }
  }

  setEffectiveTags(value: string): void {
    if (this.selectedDestinationId) {
      this.publishState.setDestinationMetadataOverride(
        this.selectedDestinationId,
        "tags",
        value,
      );
    } else {
      this.publishState.updateMetadataField("tags", value);
    }
  }

  resetDestinationMetadataToShared(): void {
    if (this.selectedDestinationId) {
      this.publishState.clearDestinationMetadataOverride(this.selectedDestinationId);
    }
  }

  get selectedDestination(): SocialAccount | null {
    if (!this.selectedDestinationId) return null;
    return (
      this.publishState
        .getDestinations()
        .find((d) => d.id === this.selectedDestinationId) ?? null
    );
  }

  /** Map destination type to API destination string (YouTube, Instagram, TikTok, Twitch). */
  getDestinationApiName(type: DestinationType): string | undefined {
    const map: Record<DestinationType, string | undefined> = {
      yt: "YouTube",
      ig: "Instagram",
      ttv: "Twitch",
      fb: "Facebook",
      s3: undefined,
      tt: "TikTok",
    };
    return map[type];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isModified(current: string, original: string): boolean {
    return current !== original;
  }

  onDestinationChange(value: string | null): void {
    this.selectedDestinationId = value?.trim() || null;
  }

  triggerAiComplete(): void {
    const destinationForApi = this.selectedDestination
      ? this.getDestinationApiName(this.selectedDestination.type)
      : undefined;
    this.publishState.triggerAiComplete(
      this.allowedRatingsForAi,
      this.selectedDestinationId ?? undefined,
      destinationForApi,
    );
  }

  setMetadataField<K extends keyof PublishMetadata>(key: K, value: PublishMetadata[K]): void {
    this.publishState.updateMetadataField(key, value);
  }
}
